import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  CircularProgress,
  Menu,
  MenuItem,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowLeft, MoreHorizontal, Plus, Shapes } from 'lucide-react';
import { deleteChat } from '../../services/AGTChatService';
import { getProjectChats } from '../../../AGTHall/services/AGTHallService';
import DeleteIcon from '@mui/icons-material/Delete';
import { useProjectContext } from '../ProjectContextProvider';
import { useTranslation } from 'react-i18next'; // Importar hook de traducción

const CategoryChip = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
  display: 'inline-flex',
  alignItems: 'center',
  height: '20px',
  padding: '0 8px',
  borderRadius: '10px',
  backgroundColor:
    isWallpaperMode
      ? 'rgba(255, 255, 255, 0.16)'
      : (theme.palette.mode === 'dark'
        ? 'rgba(3, 133, 255, 0.1)'
        : 'rgba(3, 133, 255, 0.08)'),
  color: isWallpaperMode ? '#FFFFFF' : '#0385FF',
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.2px',
  textTransform: 'capitalize',
  marginLeft: theme.spacing(1),
  };
});

const StyledButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.9)' : '#0385FF',
    color: isWallpaperMode ? '#000000' : '#FFFFFF',
    borderRadius: '20px',
    textTransform: 'none',
    padding: '6px 16px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.8)' : '#0371db',
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      marginRight: 6,
    },
  };
});

const ChatsView = ({ project, onBack, user, onChatSelect }) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const { t } = useTranslation(); // Inicializar hook de traducción
  const {
    projectChats,
    updateProjectChats,
    removeProjectChat
  } = useProjectContext();

  const [selectedChat, setSelectedChat] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuChatId, setMenuChatId] = useState(null);

  // Cargar chats al montar
  useEffect(() => {
    if (!project?.id || !user?.uid) return;

    setIsLoading(true);
    getProjectChats(user.uid, project.id)
      .then((res) => {
        updateProjectChats(project.id, res.chats || []);
      })
      .catch((err) => {
        console.error('Error fetching project chats:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [project?.id, user?.uid, updateProjectChats]);

  // MODIFICADO: No crea el chat inmediatamente, solo cambia la vista
  // El chat se creará cuando el usuario envíe el primer mensaje (evita chats vacíos)
  const handleCreateNewChat = () => {
    if (!project?.id || !user?.uid) return;

    // Resetear selección actual
    setSelectedChat(null);

    // Llamar a onChatSelect con null para indicar un nuevo chat sin crear
    if (onChatSelect) {
      onChatSelect(null);
    }
  };

  const handleChatClick = (chat) => {
    setSelectedChat(chat);
    if (onChatSelect) {
      onChatSelect({
        id: chat.id,
        title: chat.title
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleMenuOpen = (event, chat) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuChatId(chat.id);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuChatId(null);
  };

  const handleDeleteChat = async () => {
    if (!menuChatId || !user?.uid) return;
    setIsLoading(true);
  
    try {
      await deleteChat(user.uid, menuChatId);
      
      // Eliminar chat del estado global
      removeProjectChat(project.id, menuChatId);
      handleMenuClose();

      if (selectedChat?.id === menuChatId) {
        setSelectedChat(null);
        if (onBack) onBack();
      }

    } catch (error) {
      console.error('Error deleting project chat:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const currentProjectChats = projectChats[project?.id] || [];

  return (
    <Box 
      sx={{ 
        width: '100%', 
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: {
            xs: '100%',
            sm: '100%',
            md: '800px',
            lg: '900px'
          },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
          mb: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={onBack} 
              size="small" 
              sx={{ 
                mr: 1,
                color: isWallpaperMode
                  ? 'rgba(255, 255, 255, 0.8)'
                  : (theme.palette.mode === 'dark' ? 'grey.400' : 'grey.700')
              }}
            >
              <ArrowLeft />
            </IconButton>
            <Typography 
              variant="h6" 
              sx={{ 
                fontSize: '1.1rem',
                fontWeight: 500,
                color: isWallpaperMode ? '#FFFFFF' : 'inherit'
              }}
            >
              {project?.name}
            </Typography>
          </Box>

          <StyledButton
            startIcon={<Plus size={18} />}
            onClick={handleCreateNewChat}
            disabled={isLoading}
          >
            {t('projectViews.chats.newChat')}
          </StyledButton>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : currentProjectChats.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Shapes size={48} color={theme.palette.text.secondary} opacity={0.5} />
            <Typography color="text.secondary">
              {t('projectViews.chats.noChats')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ px: 0 }}>
            {[...currentProjectChats]
              .sort((a, b) => {
                const dateA = new Date(a.updatedAt || a.createdAt);
                const dateB = new Date(b.updatedAt || b.createdAt);
                return dateB - dateA;
              })
              .map((chat) => (
              <ListItem
                key={chat.id}
                button
                sx={{
                  borderRadius: '12px',
                  mb: 1.5,
                  p: 1.5,
                  backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                  WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
                  backgroundColor: chat.id === selectedChat?.id 
                    ? (isWallpaperMode
                      ? 'rgba(255, 255, 255, 0.12)'
                      : (theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.03)'))
                    : (isWallpaperMode ? 'rgba(255, 255, 255, 0.06)' : 'transparent'),
                  '&:hover': {
                    backgroundColor: isWallpaperMode
                      ? 'rgba(255, 255, 255, 0.18)'
                      : (theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(0, 0, 0, 0.05)'),
                  },
                  transition: 'background-color 0.2s ease',
                }}
                onClick={() => handleChatClick(chat)}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Shapes
                    size={20}
                    color={isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#fff' : '#000')}
                    opacity={chat.id === selectedChat?.id ? 1 : 0.7}
                  />
                </ListItemIcon>

                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: '0.95rem',
                          fontWeight: chat.id === selectedChat?.id ? 500 : 400,
                          color: isWallpaperMode ? '#FFFFFF' : 'inherit'
                        }}
                      >
                        {chat.title || t('chat.header.newChat')}
                      </Typography>

                      {chat.type === 'project' && chat.category && (
                        <CategoryChip>
                          {chat.category} {t('chat.header.projectChat', { category: '' }).trim()}
                        </CategoryChip>
                      )}
                    </Box>
                  }
                  secondary={formatDate(chat.updatedAt || chat.createdAt)}
                  secondaryTypographyProps={{
                    sx: {
                      fontSize: '0.8rem',
                      color: isWallpaperMode
                        ? 'rgba(255, 255, 255, 0.7)'
                        : (theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.5)'
                          : 'rgba(0, 0, 0, 0.5)'),
                      mt: 0.5
                    }
                  }}
                />

                <IconButton
                  sx={{ 
                    ml: 'auto',
                    color: isWallpaperMode
                      ? 'rgba(255, 255, 255, 0.7)'
                      : (theme.palette.mode === 'dark' ? 'grey.400' : 'grey.700'),
                  }}
                  onClick={(e) => handleMenuOpen(e, chat)}
                  size="small"
                >
                  <MoreHorizontal size={18} />
                </IconButton>
              </ListItem>
            ))}
          </List>
        )}

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              bgcolor: isWallpaperMode ? 'rgba(32, 32, 32, 0.9)' : 'background.paper',
              color: isWallpaperMode ? '#FFFFFF' : 'inherit',
              border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 2px 8px rgba(0, 0, 0, 0.4)'
                : '0 2px 8px rgba(0, 0, 0, 0.1)',
            }
          }}
        >
          <MenuItem
            onClick={handleDeleteChat}
            sx={{
              color: 'error.main',
              fontSize: '0.9rem',
              py: 1,
              px: 2,
              gap: 1,
            }}
          >
            <DeleteIcon fontSize="small" />
            {t('projectViews.chats.delete')}
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default ChatsView;
